/* Number formatting — §9.5. Money over $1,000 loses its decimals on the home
   screen and keeps them in detail views; percentages take one decimal; share
   counts print every decimal actually held. */

const SYMBOL = { USD: '$', PLN: 'zł', EUR: '€', HKD: 'HK$' } as const;

export type DisplayCurrency = 'USD' | 'PLN';

/* ---------- amounts hidden ---------- */

/* A switch for showing the book to somebody without showing what is in it.
 *
 * It lives here, as a module flag, rather than as a prop threaded through the
 * screens — and that is the one place in this codebase where global mutable
 * state is the safer answer. There are over a hundred call sites that print
 * money. A privacy switch that has to be remembered at each of them is a
 * switch that leaks the first time somebody adds a screen and forgets, and a
 * leak is the whole failure. Gated at the formatter, nothing can print an
 * amount without going past it.
 *
 * Set once per render from the store, in `StoreProvider`, before any child has
 * formatted anything.
 *
 * **Percentages, prices and dates are not hidden.** A weight is a shape, not a
 * sum; a share price is public and the same for everyone; and hiding them
 * would leave a screen that says nothing rather than one that says everything
 * except how much. Share counts *are* hidden, because a count beside a visible
 * price is an amount written as a multiplication. */
let hidden = false;

/** Four dots, not "hidden" — the figure keeps its shape in the layout, and a
    row does not reflow when the switch is thrown. */
const MASK = '••••';

export function setAmountsHidden(on: boolean): void {
  hidden = on;
}

export function amountsHidden(): boolean {
  return hidden;
}

/** The mask a signed figure gets: the direction survives, the size does not.
    Which way a position went is not what anybody was hiding, and §11 wants the
    direction carried by something other than colour. */
function maskSigned(v: number): string {
  return `${v > 0 ? '+' : v < 0 ? '-' : ''}${MASK}`;
}

function group(n: number, decimals: number): string {
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Home-screen money: no decimals once past a thousand. */
export function money(v: number, currency: keyof typeof SYMBOL = 'USD'): string {
  if (hidden) return MASK;
  const abs = Math.abs(v);
  const decimals = abs >= 1000 ? 0 : 2;
  const body = group(abs, decimals);
  const sign = v < 0 ? '-' : '';
  return currency === 'PLN' ? `${sign}${body} zł` : `${sign}${SYMBOL[currency]}${body}`;
}

/** Detail-view money: always two decimals. */
export function moneyExact(v: number, currency: keyof typeof SYMBOL = 'USD'): string {
  if (hidden) return MASK;
  const sign = v < 0 ? '-' : '';
  const body = group(Math.abs(v), 2);
  return currency === 'PLN' ? `${sign}${body} zł` : `${sign}${SYMBOL[currency]}${body}`;
}

/** Money with an explicit + on gains, for change figures. */
export function moneySigned(v: number, currency: keyof typeof SYMBOL = 'USD'): string {
  if (hidden) return maskSigned(v);
  return (v > 0 ? '+' : '') + money(v, currency);
}

/** The same, keeping both decimals — for detail views (§9.5). */
export function moneyExactSigned(v: number, currency: keyof typeof SYMBOL = 'USD'): string {
  if (hidden) return maskSigned(v);
  return (v > 0 ? '+' : '') + moneyExact(v, currency);
}

export function pct(v: number, decimals = 1): string {
  return `${v.toFixed(decimals)}%`;
}

export function pctSigned(v: number, decimals = 1): string {
  return `${v > 0 ? '+' : ''}${v.toFixed(decimals)}%`;
}

/** Never round 208.6132 to 209 (§9.5). Trailing zeros are dropped, so a whole
    share count still prints as "456". */
export function shares(v: number): string {
  if (hidden) return MASK;
  if (Number.isInteger(v)) return v.toLocaleString('en-US');
  const [whole = '0', frac = ''] = v.toFixed(8).replace(/0+$/, '').split('.');
  const groupedWhole = Number(whole).toLocaleString('en-US');
  return frac ? `${groupedWhole}.${frac}` : groupedWhole;
}

/** A share count with the ticker as its unit: "668 ZETA", not "668 sh". You do
    not hold shares in the abstract, you hold that company. */
export function sharesOf(count: number, ticker: string): string {
  return `${shares(count)} ${ticker}`;
}

export function price(v: number, currency: keyof typeof SYMBOL = 'USD'): string {
  const decimals = Math.abs(v) >= 1000 ? 2 : Math.abs(v) >= 1 ? 2 : 4;
  const body = group(Math.abs(v), decimals);
  const sign = v < 0 ? '-' : '';
  return currency === 'PLN' ? `${sign}${body} zł` : `${sign}${SYMBOL[currency]}${body}`;
}

/* ---------- time ---------- */

/** "prices from 2h ago" (§3.3). Returns null when the mark is fresh enough that
    saying anything would be noise. */
export function ageLabel(iso: string | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 90_000) return null;
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

const DAY_MS = 86_400_000;

/** Whole days from today to an ISO date. Negative means the date has passed. */
export function daysUntil(iso: string, now = new Date()): number {
  const target = new Date(`${iso.slice(0, 10)}T00:00:00Z`).getTime();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((target - today) / DAY_MS);
}

export function daysSince(iso: string, now = new Date()): number {
  return -daysUntil(iso, now);
}

/** "14 Mar" / "14 Mar 2027" when the year differs from the current one. */
export function shortDate(iso: string, now = new Date()): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', timeZone: 'UTC' };
  if (d.getUTCFullYear() !== now.getUTCFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-GB', opts);
}

export function dateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/** The stamp over a scrubbed point on the chart. On a window that keeps every
    reading the hour is part of what is being pointed at, so it is printed —
    a day's line is a sequence of moments, and "19 Aug" three times over is not
    an answer to where the finger is. On a wider window one reading stands for a
    whole day and its hour describes when the app was open rather than anything
    about the market, so it is left off. Shown in the reader's own clock, which
    is the one they are holding. */
export function stamp(iso: string, withTime: boolean, now = new Date()): string {
  return withTime ? dateTime(iso) : shortDate(iso, now);
}

export function todayIso(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** "3 positions" / "1 position" */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Drops trailing sentence punctuation so a quoted phrase can be folded into a
    larger sentence without doubling the full stop. The user writes their own
    abort metric, and it may or may not already end in one. */
export function asClause(text: string): string {
  return text.trim().replace(/[.!;:,]+$/, '');
}

/** Joins a list the way a sentence would: "A, B and C". */
export function sentenceList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/* Whether a date input actually holds a day.
 *
 * A `type="date"` field can be cleared, and every form that stamps
 * `${at}T12:00:00.000Z` will happily build "T12:00:00.000Z" out of an empty
 * one. That is not a date and nothing downstream says so: its first ten
 * characters are "T12:00:00." as a marker date, it sorts after every real
 * timestamp so FIFO matches it last, and `new Date` of it throws. A holding
 * written the same way carries `openedAt: ""`, which the rebuilt value line
 * then picks as the earliest date it has ever held.
 *
 * So the forms ask this before they let the button work. */
export function isDay(v: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  /* Round-tripped rather than merely parsed: the 31st of February parses fine
     and rolls over to the 3rd of March, so a shape check and a NaN check both
     pass something that is not the day it says it is. */
  const d = new Date(`${v}T12:00:00.000Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}
